import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Users, Plus, Pencil, Trash2, Key, Shield, Building2, 
  Search, Filter, UserCheck, UserX, Mail, Phone, MoreVertical
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-red-100 text-red-700', description: 'Full platform access' },
  { value: 'company_admin', label: 'Company Admin', color: 'bg-purple-100 text-purple-700', description: 'Company management, hotels & users' },
  { value: 'finance_admin', label: 'Finance Admin', color: 'bg-green-100 text-green-700', description: 'Finance & accounting access' },
  { value: 'hotel_admin', label: 'Hotel Admin', color: 'bg-blue-100 text-blue-700', description: 'Full hotel management' },
  { value: 'manager', label: 'Manager', color: 'bg-indigo-100 text-indigo-700', description: 'Operations management' },
  { value: 'front_office', label: 'Front Office', color: 'bg-amber-100 text-amber-700', description: 'Check-in/out, bookings' },
  { value: 'service_staff', label: 'Service Staff', color: 'bg-teal-100 text-teal-700', description: 'Tasks, requests, room service' },
  { value: 'service_manager', label: 'Service Manager', color: 'bg-cyan-100 text-cyan-700', description: 'Service & housekeeping' },
  { value: 'housekeeping', label: 'Housekeeping', color: 'bg-lime-100 text-lime-700', description: 'Room cleaning, maintenance' },
  { value: 'restaurant_staff', label: 'Restaurant Staff', color: 'bg-pink-100 text-pink-700', description: 'POS, menu management' },
];

// All roles for display/filtering purposes
const ALL_ROLE_OPTIONS = ROLE_OPTIONS;

export default function SuperAdminUserManagement() {
  const [users, setUsers] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterHotel, setFilterHotel] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [assignHotelsOpen, setAssignHotelsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'company_admin',
    company_ids: [],
    hotel_id: ''
  });
  const [newPassword, setNewPassword] = useState('');
  const [selectedHotelIds, setSelectedHotelIds] = useState([]);
  const [primaryHotelId, setPrimaryHotelId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, hotelsRes, companiesRes] = await Promise.all([
        api.get('/super-admin/users'),
        api.get('/super-admin/hotels'),
        api.get('/companies')
      ]);
      setUsers(usersRes.data);
      setHotels(hotelsRes.data);
      setCompanies(companiesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'company_admin',
      company_ids: [],
      hotel_id: ''
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      // Validate company selection for company_admin
      if (formData.role === 'company_admin' && formData.company_ids.length === 0) {
        toast.error('Please select at least one company for Company Admin');
        return;
      }
      
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role
      };
      
      // Add company_ids for company_admin
      if (formData.role === 'company_admin' && formData.company_ids.length > 0) {
        payload.company_ids = formData.company_ids;
        // Set primary company_id as the first selected company
        payload.company_id = formData.company_ids[0];
      }
      
      await api.post('/super-admin/users', payload);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams();
      if (formData.name) params.append('name', formData.name);
      if (formData.email) params.append('email', formData.email);
      if (formData.role) params.append('role', formData.role);
      
      // For company_admin or finance_admin, save company_id
      if ((formData.role === 'company_admin' || formData.role === 'finance_admin') && formData.company_id) {
        params.append('company_id', formData.company_id);
      }
      
      // For hotel-level roles, save hotel_id
      if (!['super_admin', 'company_admin', 'finance_admin'].includes(formData.role)) {
        if (formData.hotel_id !== undefined) {
          params.append('hotel_id', formData.hotel_id || '');
        }
      }
      
      await api.put(`/super-admin/users/${selectedUser.id}?${params.toString()}`);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) return;
    try {
      await api.delete(`/super-admin/users/${user.id}`);
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete user'));
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const res = await api.post(`/super-admin/users/${user.id}/toggle-active`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to toggle status'));
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/super-admin/users/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(newPassword)}`);
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to reset password'));
    }
  };

  const handleAssignHotels = async () => {
    try {
      await api.post(`/super-admin/users/${selectedUser.id}/assign-hotels`, null, {
        params: {
          hotel_ids: selectedHotelIds,
          primary_hotel_id: primaryHotelId || undefined
        },
        paramsSerializer: params => {
          const searchParams = new URLSearchParams();
          if (params.hotel_ids) {
            params.hotel_ids.forEach(id => searchParams.append('hotel_ids', id));
          }
          if (params.primary_hotel_id) {
            searchParams.append('primary_hotel_id', params.primary_hotel_id);
          }
          return searchParams.toString();
        }
      });
      toast.success('Hotels assigned successfully');
      setAssignHotelsOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to assign hotels'));
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      company_ids: user.company_ids || [],
      company_id: user.company_id || (user.company_ids?.[0] || ''),
      hotel_id: user.hotel_id || ''
    });
    setEditDialogOpen(true);
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const openAssignHotelsDialog = (user) => {
    setSelectedUser(user);
    setSelectedHotelIds(user.assigned_hotels?.map(h => h.id || h) || []);
    setPrimaryHotelId(user.hotel_id || '');
    setAssignHotelsOpen(true);
  };

  const getRoleBadge = (role) => {
    const roleInfo = ROLE_OPTIONS.find(r => r.value === role);
    return roleInfo || { label: role, color: 'bg-gray-100 text-gray-700' };
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    const matchesHotel = filterHotel === 'all' || user.hotel_id === filterHotel;
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'active' && user.is_active) ||
                         (filterStatus === 'inactive' && !user.is_active);
    return matchesSearch && matchesRole && matchesHotel && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="super-admin-users">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope' }}>
            User Management
          </h1>
          <p className="text-muted-foreground mt-1">Manage all users across the platform</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="create-user-dialog">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Role *</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v, company_ids: [], hotel_id: ''})}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-muted-foreground">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.role === 'company_admin' && (
                <div className="space-y-2">
                  <Label>Assign Companies *</Label>
                  <p className="text-xs text-muted-foreground mb-2">Select companies this admin can manage</p>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-2">
                    {companies.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">No companies created yet. Create a company first.</p>
                    ) : (
                      companies.map(company => {
                        const isSelected = (formData.company_ids || []).includes(company.id);
                        return (
                          <div 
                            key={company.id}
                            className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-primary bg-primary/5' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onClick={() => {
                              const currentIds = formData.company_ids || [];
                              const newIds = isSelected
                                ? currentIds.filter(id => id !== company.id)
                                : [...currentIds, company.id];
                              setFormData({...formData, company_ids: newIds});
                            }}
                          >
                            <div className={`h-4 w-4 rounded border flex items-center justify-center ${
                              isSelected ? 'bg-primary border-primary' : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                                  <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{company.name}</p>
                              <p className="text-xs text-muted-foreground">{company.email}</p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                  {formData.company_ids.length > 0 && (
                    <p className="text-xs text-green-600">{formData.company_ids.length} company(s) selected</p>
                  )}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1" data-testid="submit-create-user">Create User</Button>
                <Button type="button" variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.is_active).length}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'hotel_admin').length}</p>
                <p className="text-xs text-muted-foreground">Hotel Admins</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center">
                <UserX className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.filter(u => !u.is_active).length}</p>
                <p className="text-xs text-muted-foreground">Inactive</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLE_OPTIONS.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterHotel} onValueChange={setFilterHotel}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Hotel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Hotels</SelectItem>
                {hotels.map(h => (
                  <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Role</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Hotel</th>
                    <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-right p-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => {
                    const roleInfo = getRoleBadge(user.role);
                    return (
                      <tr key={user.id} className="border-b hover:bg-muted/50" data-testid={`user-row-${user.id}`}>
                        <td className="p-3">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                              <span className="text-sm font-medium text-primary">
                                {user.name?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium">{user.name}</p>
                              <p className="text-sm text-muted-foreground">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={roleInfo.color}>{roleInfo.label}</Badge>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            <span className="text-sm">{user.hotel_name || 'N/A'}</span>
                            {user.assigned_hotel_names && user.assigned_hotel_names.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {user.assigned_hotel_names.slice(0, 2).map((name, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{name}</Badge>
                                ))}
                                {user.assigned_hotel_names.length > 2 && (
                                  <Badge variant="outline" className="text-xs">+{user.assigned_hotel_names.length - 2}</Badge>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <Badge className={user.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="p-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit User
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => openResetPasswordDialog(user)}>
                                <Key className="h-4 w-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              {user.role === 'hotel_admin' && (
                                <DropdownMenuItem onClick={() => openAssignHotelsDialog(user)}>
                                  <Building2 className="h-4 w-4 mr-2" />
                                  Assign Hotels
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                                {user.is_active ? (
                                  <>
                                    <UserX className="h-4 w-4 mr-2" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <UserCheck className="h-4 w-4 mr-2" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600" 
                                onClick={() => handleDelete(user)}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="edit-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                data-testid="edit-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                <SelectTrigger data-testid="edit-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(formData.role === 'company_admin' || formData.role === 'finance_admin') && (
              <div className="space-y-2">
                <Label>Company</Label>
                <Select value={formData.company_id || "none"} onValueChange={(v) => setFormData({...formData, company_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="edit-user-company">
                    <SelectValue placeholder="Select a company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Company</SelectItem>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-purple-500" />
                          {company.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {formData.role !== 'super_admin' && formData.role !== 'company_admin' && formData.role !== 'finance_admin' && (
              <div className="space-y-2">
                <Label>Primary Hotel</Label>
                <Select value={formData.hotel_id || "none"} onValueChange={(v) => setFormData({...formData, hotel_id: v === "none" ? "" : v})}>
                  <SelectTrigger data-testid="edit-user-hotel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Hotel</SelectItem>
                    {hotels.map(hotel => (
                      <SelectItem key={hotel.id} value={hotel.id}>{hotel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1" data-testid="save-edit-user">Save Changes</Button>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Reset password for <strong>{selectedUser?.name}</strong>
            </p>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex gap-3">
              <Button onClick={handleResetPassword} className="flex-1" data-testid="confirm-reset-password">
                Reset Password
              </Button>
              <Button variant="outline" onClick={() => setResetPasswordOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Hotels Dialog */}
      <Dialog open={assignHotelsOpen} onOpenChange={setAssignHotelsOpen}>
        <DialogContent className="max-w-md" data-testid="assign-hotels-dialog">
          <DialogHeader>
            <DialogTitle>Assign Hotels to {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Hotels</Label>
              <div className="border rounded-lg p-3 max-h-[200px] overflow-y-auto space-y-2">
                {hotels.map(hotel => (
                  <div key={hotel.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`hotel-${hotel.id}`}
                      checked={selectedHotelIds.includes(hotel.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedHotelIds([...selectedHotelIds, hotel.id]);
                        } else {
                          setSelectedHotelIds(selectedHotelIds.filter(id => id !== hotel.id));
                          if (primaryHotelId === hotel.id) setPrimaryHotelId('');
                        }
                      }}
                    />
                    <label htmlFor={`hotel-${hotel.id}`} className="text-sm cursor-pointer flex-1">
                      {hotel.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            {selectedHotelIds.length > 0 && (
              <div className="space-y-2">
                <Label>Primary Hotel</Label>
                <Select value={primaryHotelId || "none"} onValueChange={(v) => setPrimaryHotelId(v === "none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select primary hotel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {hotels.filter(h => selectedHotelIds.includes(h.id)).map(hotel => (
                      <SelectItem key={hotel.id} value={hotel.id}>{hotel.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button onClick={handleAssignHotels} className="flex-1" data-testid="confirm-assign-hotels">
                Save Assignments
              </Button>
              <Button variant="outline" onClick={() => setAssignHotelsOpen(false)}>Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
